import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { cashTender, formatMoney, moneyFromCents } from '@wodi/domain';
import type { DepositTender, StayBilling } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  MoneyField,
  Notice,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { billingKeys, currentShiftQuery } from '../api/billing.ts';
import {
  applyPackage,
  issueFinalBill,
  issueInterimBill,
  packagesQuery,
  removeStayPackage,
  stayBillingKeys,
  takeDeposit,
} from '../api/inpatient-billing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { METHOD_LABELS, money, type PaymentMethodKey } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, ShortcutHint } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from '../inpatient/inpatient.module.css';
import { StayBillPrinter } from './bill-print.tsx';
import { balanceWords, staySentence } from './words.ts';

// The money side of an inpatient stay (docs/plans/phase2-inpatient-billing.md §6), shared by
// the stay page and the cashier: the balance, deposits, packages and the stay's bills, with
// the actions the person's permissions allow.

const REFERENCE_LABEL: Record<Exclude<PaymentMethodKey, 'cash'>, string> = {
  mpesa: 'M-Pesa code',
  card: 'Card slip reference',
  bank_transfer: 'Bank reference',
};

async function refreshStay(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: stayBillingKeys.all }),
    queryClient.invalidateQueries({ queryKey: billingKeys.all }),
    queryClient.invalidateQueries({ queryKey: ['inpatient'] }),
  ]);
}

/**
 * Take a deposit (plan §1.1 item 1): cash with the cash received and the change, or M-Pesa,
 * card or bank by reference. It needs the cashier's open shift; the receipt prints from the
 * visit bill's payments. Ctrl/⌘Enter takes it.
 */
export function DepositDialog({
  billing,
  onClose,
}: {
  billing: StayBilling;
  onClose: (receiptId: string | null) => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const shift = useQuery(currentShiftQuery);
  const [method, setMethod] = useState<PaymentMethodKey>('cash');
  const [amount, setAmount] = useState<bigint | null>(null);
  const [tendered, setTendered] = useState<bigint | null>(null);
  const [reference, setReference] = useState('');
  const [requestKey] = useState(() => crypto.randomUUID());
  const [tried, setTried] = useState(false);

  const cash =
    method === 'cash' && amount !== null && tendered !== null
      ? cashTender({ amount: moneyFromCents(amount), tendered: moneyFromCents(tendered) })
      : null;
  const problem =
    amount === null || amount <= 0n
      ? 'Enter the deposit.'
      : method === 'cash'
        ? tendered === null
          ? 'Enter the cash received.'
          : cash !== null && !cash.ok
            ? 'The cash received is less than the deposit.'
            : null
        : reference.trim().length < 3
          ? `Enter the ${REFERENCE_LABEL[method].toLowerCase()}.`
          : null;
  const noShift = shift.isSuccess && shift.data.shift === null;

  const submit = useMutation({
    mutationFn: () => {
      const tender: DepositTender =
        method === 'cash'
          ? {
              method: 'cash',
              amountCents: (amount ?? 0n).toString(),
              tenderedCents: (tendered ?? 0n).toString(),
            }
          : {
              method,
              amountCents: (amount ?? 0n).toString(),
              reference: reference.trim(),
            };
      return takeDeposit(billing.admission.id, { clientRequestId: requestKey, tender });
    },
    onSuccess: async (result) => {
      await refreshStay(queryClient);
      toasts.show({
        title: 'Deposit received',
        description:
          result.change !== null && result.change.cents !== '0'
            ? `Change ${money(result.change)}`
            : 'Receipt printing',
      });
      onClose(result.deposit.receipt?.id ?? null);
    },
  });
  const go = () => {
    setTried(true);
    if (problem === null && online && !noShift && !submit.isPending) submit.mutate();
  };
  useShortcut('mod+enter', go, { allowInFields: true, description: 'Take deposit' });

  return (
    <Dialog
      title="Take deposit"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose(null);
      }}
    >
      <div className={styles.form}>
        <p className={styles.meta}>
          The patient's money held against the stay. It is applied to the final bill, and whatever
          is left is refunded with approval.
        </p>
        {noShift ? (
          <Notice tone="info">Open a cashier shift before taking a deposit.</Notice>
        ) : null}
        <div className={styles.row}>
          <Select
            label="Method"
            options={(Object.keys(METHOD_LABELS) as PaymentMethodKey[]).map((m) => ({
              id: m,
              label: METHOD_LABELS[m],
            }))}
            value={method}
            onChange={(v) => {
              setMethod(v as PaymentMethodKey);
            }}
          />
          <MoneyField
            label="Deposit"
            value={amount}
            onChange={setAmount}
            autoFocus
            {...(tried && problem === 'Enter the deposit.' ? { errorMessage: problem } : {})}
          />
          {method === 'cash' ? (
            <MoneyField
              label="Cash received"
              value={tendered}
              onChange={setTendered}
              {...(tried && problem !== null && problem !== 'Enter the deposit.'
                ? { errorMessage: problem }
                : {})}
            />
          ) : (
            <TextField
              label={REFERENCE_LABEL[method]}
              value={reference}
              onChange={setReference}
              {...(tried && problem !== null && problem !== 'Enter the deposit.'
                ? { errorMessage: problem }
                : {})}
            />
          )}
        </div>
        {cash?.ok ? (
          <p className={styles.meta}>{`Change ${formatMoney(cash.value.change)}`}</p>
        ) : null}
        {/* TODO(business) IB1: no deposit amount rule yet; any amount is taken. */}
        {!online ? (
          <Notice tone="info">
            Taking a deposit needs a connection to Wodi. Write a receipt from the receipt book and
            enter it when Wodi is back.
          </Notice>
        ) : null}
        {submit.isError ? (
          <Notice tone="error">
            {errorText(submit.error, "The deposit wasn't recorded. Try again.")}
          </Notice>
        ) : null}
        <ShortcutHint combo="mod+enter" action="take the deposit" />
      </div>
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onClose(null);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={go}
          isPending={submit.isPending}
          pendingLabel="Taking deposit"
          isDisabled={!online || noShift}
        >
          Take deposit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddPackageDialog({ billing, onClose }: { billing: StayBilling; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const packages = useQuery(packagesQuery);
  const [packageId, setPackageId] = useState<string | null>(null);
  const [requestKey] = useState(() => crypto.randomUUID());
  const applied = new Set(
    billing.packages.filter((p) => p.status === 'active').map((p) => p.packageId),
  );
  const options = (packages.data?.items ?? []).filter((p) => !applied.has(p.id));
  const chosen = options.find((p) => p.id === packageId);
  const submit = useMutation({
    mutationFn: () => applyPackage(billing.admission.id, packageId ?? '', requestKey),
    onSuccess: async () => {
      await refreshStay(queryClient);
      toasts.show({ title: 'Package added', description: chosen?.name ?? '' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Add package"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        {packages.isPending ? <LoadingBlock label="Loading packages" rows={2} /> : null}
        {packages.isError ? (
          <Notice tone="error">
            {errorText(packages.error, "Packages couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {packages.isSuccess && options.length === 0 ? (
          <Notice tone="info">
            No packages to add. Packages are set up in Settings, Packages.
          </Notice>
        ) : null}
        {options.length > 0 ? (
          <Select
            label="Package"
            placeholder="Choose one"
            options={options.map((p) => ({ id: p.id, label: p.name }))}
            value={packageId}
            onChange={(v) => {
              setPackageId(v === null ? null : String(v));
            }}
          />
        ) : null}
        {chosen === undefined ? null : (
          <p className={styles.meta}>
            {`Charged as ${chosen.item.name}. Covers ${chosen.covers
              .map((c) =>
                c.maxQuantity === null ? c.name : `${c.name} (up to ${String(c.maxQuantity)})`,
              )
              .join(', ')}. Covered charges not yet on a bill are replaced.`}
          </p>
        )}
        {submit.isError ? (
          <Notice tone="error">
            {errorText(submit.error, "The package wasn't added. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online || packageId === null}
          isPending={submit.isPending}
          pendingLabel="Adding"
          onPress={() => {
            submit.mutate();
          }}
        >
          Add package
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemovePackageDialog({
  stayPackageId,
  name,
  onClose,
}: {
  stayPackageId: string;
  name: string;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const submit = useMutation({
    mutationFn: () => removeStayPackage(stayPackageId, reason.trim()),
    onSuccess: async () => {
      await refreshStay(queryClient);
      toasts.show({ title: 'Package removed', description: name });
      onClose();
    },
  });
  return (
    <Dialog
      title="Remove package"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p className={styles.meta}>
          {`Only for a package added in error. ${name} is taken off and the charges it replaced are charged again.`}
        </p>
        <TextArea label="Why is it removed?" value={reason} onChange={setReason} />
        {submit.isError ? (
          <Notice tone="error">
            {errorText(submit.error, "The package wasn't removed. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep package
        </Button>
        <Button
          variant="destructive"
          isDisabled={!online || reason.trim().length < 5}
          isPending={submit.isPending}
          pendingLabel="Removing"
          onPress={() => {
            submit.mutate();
          }}
        >
          Remove package
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FinalBillDialog({
  billing,
  onClose,
}: {
  billing: StayBilling;
  onClose: (billId: string | null) => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const submit = useMutation({
    mutationFn: () => issueFinalBill(billing.admission.id),
    onSuccess: async (result) => {
      await refreshStay(queryClient);
      toasts.show({ title: 'Final bill made', description: result.bill.number });
      onClose(result.bill.id);
    },
  });
  return (
    <Dialog
      title="Final bill"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose(null);
      }}
    >
      <div className={styles.form}>
        <p className={styles.meta}>
          Every charge of the stay goes on the bill and the deposits held are applied to what is
          owed. What is left is paid at the cashier, or refunded with approval. This is done once.
        </p>
        {submit.isError ? (
          <Notice tone="error">
            {errorText(submit.error, "The final bill wasn't made. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onClose(null);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={submit.isPending}
          pendingLabel="Making the bill"
          onPress={() => {
            submit.mutate();
          }}
        >
          Make final bill
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type Open =
  | { kind: 'deposit' }
  | { kind: 'package' }
  | { kind: 'remove'; id: string; name: string }
  | { kind: 'final' }
  | null;

/**
 * The stay's bill as a section: the balance sentence, the totals, deposits, packages and
 * bills. `D` opens "Take deposit" when it is allowed.
 */
export function StayBillingSection({
  billing,
  place,
}: {
  billing: StayBilling;
  /** Where it is shown: the stay page links to the cashier, the cashier to the stay. */
  place: 'stay' | 'cashier';
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<Open>(null);
  const [printing, setPrinting] = useState<{ billId: string; auto: boolean } | null>(null);
  const allowed = (p: Parameters<typeof can>[1]) => session !== null && can(session, p);
  const a = billing.admission;
  const t = billing.totals;
  const mayDeposit = allowed('payments.take') && a.status === 'admitted';
  const mayFinal =
    allowed('payments.take') && a.status === 'discharged' && billing.finalBillId === null;
  const mayPackage = allowed('inpatient.package.apply') && a.status === 'admitted';

  const interim = useMutation({
    mutationFn: () => issueInterimBill(a.id),
    onSuccess: async (result) => {
      await refreshStay(queryClient);
      toasts.show({ title: 'Interim bill made', description: result.bill.number });
      setPrinting({ billId: result.bill.id, auto: true });
    },
  });

  useShortcut(
    'd',
    outsideDialogs(() => {
      setOpen({ kind: 'deposit' });
    }),
    { enabled: mayDeposit && online && open === null, description: 'Take deposit' },
  );

  const { label, amount } = balanceWords(t);
  return (
    <Section
      title={place === 'stay' ? 'Bill' : 'Inpatient stay'}
      description={staySentence(billing)}
      actions={
        <>
          {mayDeposit ? (
            <Button
              variant="primary"
              shortcut="d"
              isDisabled={!online}
              onPress={() => {
                setOpen({ kind: 'deposit' });
              }}
            >
              Take deposit
            </Button>
          ) : null}
          {mayFinal ? (
            <Button
              variant="primary"
              isDisabled={!online}
              onPress={() => {
                setOpen({ kind: 'final' });
              }}
            >
              Final bill
            </Button>
          ) : null}
          {a.status !== 'cancelled' ? (
            <Button
              isDisabled={!online}
              isPending={interim.isPending}
              pendingLabel="Making the bill"
              onPress={() => {
                interim.mutate();
              }}
            >
              Interim bill
            </Button>
          ) : null}
          {mayPackage ? (
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setOpen({ kind: 'package' });
              }}
            >
              Add package
            </Button>
          ) : null}
        </>
      }
    >
      <dl className={styles.facts} aria-label="Stay totals">
        <dt>{label}</dt>
        <dd className={styles.money}>{amount}</dd>
        <dt>Charged</dt>
        <dd className={styles.money}>{money(t.charged)}</dd>
        {billing.payer.kind === 'cash' ? null : (
          <>
            <dt>{`${billing.payer.name} pays`}</dt>
            <dd className={styles.money}>{money(t.payerAmount)}</dd>
          </>
        )}
        <dt>Patient's share</dt>
        <dd className={styles.money}>{money(t.patientAmount)}</dd>
        {t.adjusted.cents === '0' ? null : (
          <>
            <dt>Waived or discounted</dt>
            <dd className={styles.money}>{money(t.adjusted)}</dd>
          </>
        )}
        <dt>Paid</dt>
        <dd className={styles.money}>{money(t.paid)}</dd>
        <dt>Deposits received</dt>
        <dd className={styles.money}>{money(t.depositsReceived)}</dd>
        <dt>Deposit held</dt>
        <dd className={styles.money}>{money(t.depositHeld)}</dd>
        {a.dischargeBalance === null ? null : (
          <>
            <dt>Owed at discharge</dt>
            <dd className={styles.money}>{money(a.dischargeBalance)}</dd>
          </>
        )}
      </dl>
      {interim.isError ? (
        <Notice tone="error">
          {errorText(interim.error, "The interim bill wasn't made. Try again.")}
        </Notice>
      ) : null}
      {t.balanceState === 'refund_due' && billing.finalBillId !== null ? (
        <Notice tone="info">
          {place === 'cashier'
            ? `Refund due ${amount}. Ask for a refund on the deposit payment in the bill below; a second person approves it.`
            : `Refund due ${amount}. The cashier asks for the refund; a second person approves it.`}
        </Notice>
      ) : null}

      <Table flat dense aria-label="Deposits">
        <TableHeader>
          <Column isRowHeader>Receipt</Column>
          <Column>Received</Column>
          <Column>Method</Column>
          <Column>Amount</Column>
          <Column>Applied</Column>
          <Column>Refunded</Column>
          <Column>Held</Column>
        </TableHeader>
        <TableBody
          items={billing.deposits}
          renderEmptyState={() => 'No deposit taken for this stay.'}
        >
          {(d) => (
            <Row id={d.id}>
              <Cell>
                <span className="wodi-id">{d.receipt?.number ?? ''}</span>
              </Cell>
              <Cell>{`${formatInstant(d.receivedAt)}, ${d.cashier.name}`}</Cell>
              <Cell>
                {METHOD_LABELS[d.method]}
                {d.reference === null ? null : (
                  <>
                    {' '}
                    <span className="wodi-id">{d.reference}</span>
                  </>
                )}
              </Cell>
              <Cell>
                <span className={styles.money}>{money(d.amount)}</span>
              </Cell>
              <Cell>
                <span className={styles.money}>{money(d.applied)}</span>
              </Cell>
              <Cell>
                <span className={styles.money}>{money(d.refunded)}</span>
              </Cell>
              <Cell>
                <span className={styles.money}>{money(d.held)}</span>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>

      {billing.packages.length === 0 ? null : (
        <Table flat dense aria-label="Packages">
          <TableHeader>
            <Column isRowHeader>Package</Column>
            <Column>Added</Column>
            <Column>Price</Column>
            <Column>Charges replaced</Column>
            <Column>State</Column>
            <Column>Action</Column>
          </TableHeader>
          <TableBody items={billing.packages}>
            {(p) => (
              <Row id={p.id}>
                <Cell>{p.name}</Cell>
                <Cell>{`${formatInstant(p.appliedAt)}, ${p.appliedBy.name}`}</Cell>
                <Cell>
                  <span className={styles.money}>
                    {p.charge?.total === null || p.charge === null
                      ? 'Not priced'
                      : money(p.charge.total)}
                  </span>
                </Cell>
                <Cell>{String(p.coveredCount)}</Cell>
                <Cell>
                  {p.status === 'active' ? (
                    <Tag state="active">Active</Tag>
                  ) : (
                    <Tag state="neutral">{`Removed: ${p.removeReason ?? ''}`}</Tag>
                  )}
                </Cell>
                <Cell>
                  {p.removable && allowed('inpatient.package.apply') ? (
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      onPress={() => {
                        setOpen({ kind: 'remove', id: p.id, name: p.name });
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      )}

      <Table flat dense aria-label="Stay bills">
        <TableHeader>
          <Column isRowHeader>Bill</Column>
          <Column>Kind</Column>
          <Column>Issued</Column>
          <Column>Balance then</Column>
          <Column>Print</Column>
        </TableHeader>
        <TableBody items={billing.bills} renderEmptyState={() => 'No interim or final bill yet.'}>
          {(b) => (
            <Row id={b.id}>
              <Cell>
                <span className="wodi-id">{b.number}</span>
              </Cell>
              <Cell>{b.kind === 'final' ? 'Final' : 'Interim'}</Cell>
              <Cell>{`${formatInstant(b.issuedAt)}, ${b.issuedBy.name}`}</Cell>
              <Cell>
                <span className={styles.money}>
                  {
                    balanceWords({
                      balance: b.balance,
                      balanceState:
                        BigInt(b.balance.cents) > 0n
                          ? 'owes'
                          : BigInt(b.balance.cents) < 0n
                            ? 'refund_due'
                            : 'settled',
                    }).label
                  }{' '}
                  {formatMoney(
                    moneyFromCents(
                      BigInt(b.balance.cents) < 0n
                        ? -BigInt(b.balance.cents)
                        : BigInt(b.balance.cents),
                    ),
                  )}
                </span>
              </Cell>
              <Cell>
                <Button
                  dense
                  variant="quiet"
                  onPress={() => {
                    setPrinting({ billId: b.id, auto: true });
                  }}
                >
                  {`Print ${b.number}`}
                </Button>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {printing === null ? null : (
        <StayBillPrinter key={printing.billId} billId={printing.billId} auto={printing.auto} />
      )}
      <p className={styles.meta}>
        {place === 'stay' ? (
          <Link to="/cashier" search={{ visitId: a.visitId }}>
            Open at the cashier
          </Link>
        ) : allowed('inpatient.read') ? (
          <Link to="/admissions/$admissionId" params={{ admissionId: a.id }}>
            Open the stay
          </Link>
        ) : null}
      </p>

      {open?.kind === 'deposit' ? (
        <DepositDialog
          billing={billing}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open?.kind === 'package' ? (
        <AddPackageDialog
          billing={billing}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open?.kind === 'remove' ? (
        <RemovePackageDialog
          stayPackageId={open.id}
          name={open.name}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open?.kind === 'final' ? (
        <FinalBillDialog
          billing={billing}
          onClose={(billId) => {
            setOpen(null);
            if (billId !== null) setPrinting({ billId, auto: true });
          }}
        />
      ) : null}
    </Section>
  );
}
